import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getUserCapabilities, checkAccess, type UserCapabilitiesResponse, type CheckAccessParams } from '@/api/endpoints/userCapabilities'

export const useUserCapabilitiesStore = defineStore('userCapabilities', () => {
  const capabilities = ref<UserCapabilitiesResponse | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  let fetchPromise: Promise<UserCapabilitiesResponse | null> | null = null

  // Computed getters for enkel tilgang
  const isGlobalAdmin = computed(() => capabilities.value?.isGlobalAdmin ?? false)
  const isProjectAdmin = computed(() => capabilities.value?.isProjectAdmin ?? false)
  const canSaveForAll = computed(() => capabilities.value?.canSaveForAll ?? false)
  const canManageUsers = computed(() => capabilities.value?.canManageUsers ?? false)      // Kun GlobalAdmin
  const canManageRoles = computed(() => capabilities.value?.canManageRoles ?? false)      // Kun GlobalAdmin
  const canManageImports = computed(() => capabilities.value?.canManageImports ?? false)
  const canManageTables = computed(() => capabilities.value?.canManageTables ?? false)
  const canManageMenu = computed(() => capabilities.value?.canManageMenu ?? false)
  const canManageDataViews = computed(() => capabilities.value?.canManageDataViews ?? false)
  const canManageDataModels = computed(() => capabilities.value?.canManageDataModels ?? false)
  const canManageValidationRules = computed(() => capabilities.value?.canManageValidationRules ?? false)
  const canManagePowerBI = computed(() => capabilities.value?.canManagePowerBI ?? false)

  /**
   * Minst én setup-flate-kapabilitet (eller admin) — styrer tilgang til
   * Systemoppsett-dashboardet og synlighet for Systemoppsett-inngangen (#1980).
   * canSaveForAll/canManageUsers/canManageRoles er ikke setup-flater og teller ikke.
   */
  const hasAnySetupCapability = computed(() =>
    isGlobalAdmin.value
    || isProjectAdmin.value
    || canManageImports.value
    || canManageTables.value
    || canManageMenu.value
    || canManageDataViews.value
    || canManageDataModels.value
    || canManageValidationRules.value
    || canManagePowerBI.value
  )

  // Oppslagstabell for `meta.requiresCapability`-nøklene på setup-rutene (#1980).
  const setupCapabilityChecks: Record<string, () => boolean> = {
    isProjectAdmin: () => isProjectAdmin.value,
    anySetupCapability: () => hasAnySetupCapability.value,
    canManageImports: () => canManageImports.value,
    canManageTables: () => canManageTables.value,
    canManageMenu: () => canManageMenu.value,
    canManageDataViews: () => canManageDataViews.value,
    canManageDataModels: () => canManageDataModels.value,
    canManageValidationRules: () => canManageValidationRules.value,
    canManagePowerBI: () => canManagePowerBI.value
  }

  /**
   * Evaluer en `meta.requiresCapability`-nøkkel mot gjeldende kapabiliteter.
   * GlobalAdmin og prosjekt-admin bypasser alle setup-kapabiliteter
   * (gate-modellen i US 1974); ukjent nøkkel evaluerer til false (fail closed).
   */
  function hasSetupCapability (key: string): boolean {
    if (isGlobalAdmin.value || isProjectAdmin.value) {
      return true
    }
    const check = setupCapabilityChecks[key]
    return check ? check() : false
  }

  /**
   * Speil kapabilitetene fra prosjekt-bootstrapen (BootstrapCapabilities har
   * samme kontrakt som UserCapabilitiesResponse) inn i storen — kalles av
   * router-guarden slik at kapabilitetsgaten kan besluttes uten ekstra fetch,
   * også ved direkte URL-navigasjon (#1980).
   */
  function hydrateFromBootstrap (data: UserCapabilitiesResponse): void {
    capabilities.value = data
  }

  /**
   * Sjekk om bruker har tilgang til en spesifikk ressurs via backend.
   * Bruker /api/user/access-check endepunktet.
   *
   * @returns true hvis tilgang, false hvis ikke
   */
  async function hasAccessTo (params: CheckAccessParams): Promise<boolean> {
    // Admin har alltid tilgang
    if (isGlobalAdmin.value || isProjectAdmin.value) {
      return true
    }

    try {
      const response = await checkAccess(params)
      return response.hasAccess
    } catch (err) {
      // Ved 403 eller annen feil, returner false
      console.warn('[userCapabilitiesStore] Tilgangssjekk feilet:', err)
      return false
    }
  }

  async function fetchCapabilities (): Promise<UserCapabilitiesResponse | null> {
    if (capabilities.value !== null)
      return capabilities.value
    if (fetchPromise !== null)
      return fetchPromise

    error.value = null
    fetchPromise = (async () => {
      loading.value = true
      try {
        const data = await getUserCapabilities()
        capabilities.value = data
        return data
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Kunne ikke hente tillatelser'
        console.error('[userCapabilitiesStore] Feil ved henting av capabilities:', err)
        return null
      } finally {
        loading.value = false
        fetchPromise = null
      }
    })()
    return fetchPromise
  }

  async function refreshCapabilities (): Promise<UserCapabilitiesResponse | null> {
    capabilities.value = null
    fetchPromise = null
    return fetchCapabilities()
  }

  // Reset state (f.eks. ved logout eller prosjektbytte)
  function reset () {
    capabilities.value = null
    loading.value = false
    error.value = null
    fetchPromise = null
  }

  return {
    // State
    capabilities,
    loading,
    error,

    // Computed
    isGlobalAdmin,
    isProjectAdmin,
    canSaveForAll,
    canManageUsers,
    canManageRoles,
    canManageImports,
    canManageTables,
    canManageMenu,
    canManageDataViews,
    canManageDataModels,
    canManageValidationRules,
    canManagePowerBI,
    hasAnySetupCapability,

    // Actions
    fetchCapabilities,
    refreshCapabilities,
    hasAccessTo,
    hasSetupCapability,
    hydrateFromBootstrap,
    reset
  }
})
